// Чистка текста отчёта (TipTap JSON) по политике оформления:
//   - неразрывные пробелы и дефисы -> обычные, пока USE_NBSP выключен (constants.ts, как lib/sto_lib.USE_NBSP):
//     Word показывает их «°» в режиме знаков форматирования и не растягивает при выравнивании по ширине;
//   - полужирный вне заголовков (СТО: полужирные только заголовки и структурные элементы - стилем, головка таблицы
//     4.9.8 и титул по форме): у листов «Задание», «Реферат», «Аннотация» - весь; в основном тексте - метка в начале
//     абзаца («Цель работы:», «Упражнение 1.») и, при вставке/импорте, абзац-«заголовок» целиком полужирный.
//     Выделение слова внутри предложения (Ctrl+B) в тексте остаётся, но writer при экспорте его снимает (по профилю
//     полужирные только заголовки, головка таблицы и титул) - живая проверка отмечает такое место (BODY-BOLD,
//     sto_intel/inlineLint.ts) с кнопкой «Снять полужирный». Слово «Пример/Примеры» в начале примера (4.15.2 -
//     полужирным курсивом) остаётся и в Word.
// Применяется при вставке (paste_import), открытии проекта (core/project.ts) - так же, как на сервере
// (stoweb/model.py plain_spaces_deep, stoweb/pasterefs.py unbold_*).
import type { JSONContent as J } from '@tiptap/core';
import { plainSpacesDeep } from './constants';

const isBold = (n: J) => (n.marks || []).some((m) => m.type === 'bold');
const noBold = (n: J): J => {
  const marks = (n.marks || []).filter((m) => m.type !== 'bold');
  const out: J = { ...n };
  if (marks.length) out.marks = marks; else delete out.marks;
  return out;
};

/** Весь текст без полужирного (листы перед текстом). Тот же объект, если полужирного нет. */
export function unboldAll<T extends J | null | undefined>(doc: T): T {
  if (!doc) return doc;
  let changed = false;
  const walk = (n: J): J => {
    let out = n;
    if (n.type === 'text' && isBold(n)) { out = noBold(n); changed = true; }
    if (n.content) {
      const c = n.content.map(walk);
      if (c.some((x, i) => x !== n.content![i])) out = { ...out, content: c };
    }
    if (n.type === 'footnote' && Array.isArray(n.attrs?.content)) {
      const c = (n.attrs!.content as J[]).map(walk);
      if (c.some((x, i) => x !== n.attrs!.content[i])) out = { ...out, attrs: { ...out.attrs, content: c } };
    }
    return out;
  };
  const res = walk(doc as J);
  return (changed ? res : doc) as T;
}

// пример «Пример – …» / «Примеры» (как его находит sto_check): слово «Пример/Примеры» по 4.15.2 - полужирным курсивом
const EXAMPLE_RE = /^Примеры?(?:\s*(?:[–—:.]|$)|\s+-\s|\s+\d+(?!\d))/;

/** Строчное содержимое абзаца без полужирной метки в начале («Цель работы:», «Упражнение 1.», «Вывод –») или, если
 *  whole, без полужирного у абзаца, полужирного целиком. Абзац-пример не трогается. null - менять нечего. */
function unboldPara(content: J[], whole: boolean): J[] | null {
  const texts = content.filter((n) => n.type === 'text' && (n.text || '').trim());
  if (!texts.length || !isBold(texts[0])) return null;
  if (EXAMPLE_RE.test(content.map((n) => (n.type === 'text' ? n.text || '' : '')).join('').trim())) return null;
  if (texts.every(isBold)) return whole ? content.map((n) => (n.type === 'text' && isBold(n) ? noBold(n) : n)) : null;
  // метка: полужирные узлы подряд в начале абзаца, текст метки кончается «:», «.», «–»
  let k = 0;
  let label = '';
  while (k < content.length && content[k].type === 'text' && (isBold(content[k]) || !(content[k].text || '').trim())) {
    label += content[k].text || '';
    k++;
  }
  if (!/[:.–—-]\s*$/.test(label) || label.trim().length > 80) return null;
  return content.map((n, i) => (i < k && n.type === 'text' && isBold(n) ? noBold(n) : n));
}

/** Блоки основного текста (и приложений): полужирные метки в начале абзацев и позиций перечислений; whole - ещё и
 *  абзацы, целиком полужирные (вставка, импорт). Головка таблицы, заголовки, подписи не трогаются. */
export function unboldBody(blocks: J[], whole = false): J[] {
  const SKIP = new Set(['tableHeader', 'heading', 'structHeading', 'caption', 'appendixTitle', 'listing', 'formula', 'figure']);
  const walk = (n: J): J => {
    if (SKIP.has(n.type || '')) return n;
    if (n.type === 'paragraph') {
      const c = n.content ? unboldPara(n.content, whole) : null;
      return c ? { ...n, content: c } : n;
    }
    if (!n.content) return n;
    const c = n.content.map(walk);
    return c.some((x, i) => x !== n.content![i]) ? { ...n, content: c } : n;
  };
  const out = blocks.map(walk);
  return out.some((x, i) => x !== blocks[i]) ? out : blocks;
}

const FRONT_KEYS = ['zadanie', 'referat', 'annotation'] as const;

/** Метаданные: листы «Задание», «Реферат», «Аннотация» - без полужирного; строки - без неразрывных пробелов. */
export function cleanMeta<T>(meta: T): T {
  if (!meta || typeof meta !== 'object') return meta;
  let m: any = plainSpacesDeep(meta);
  for (const k of FRONT_KEYS) {
    const doc = m[k]?.doc;
    const d2 = unboldAll(doc);
    if (d2 !== doc) m = { ...m, [k]: { ...m[k], doc: d2 } };
  }
  return m;
}

/** Документ при открытии проекта: без неразрывных пробелов, без полужирных меток в начале абзацев. */
export function cleanDoc<T extends J | null | undefined>(doc: T): T {
  if (!doc) return doc;
  const d = plainSpacesDeep(doc) as J;
  const c = d.content ? unboldBody(d.content, false) : undefined;
  return (c && c !== d.content ? { ...d, content: c } : d) as T;
}
